'use client';

/**
 * FlowTime — журнал времени.
 *
 * Записи сгруппированы по дням и правятся на месте. Ручное добавление
 * нужно не для красоты: таймер забывают включить, и без возможности
 * дописать час задним числом журнал перестаёт отражать реальность,
 * а вместе с ним теряют смысл и отчёты.
 */

import { useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import type { FlowTimeApi } from '@/components/datahunter/use-flowtime';
import type { FlowEntry, TimerMode } from '@/lib/flowtime-storage';
import { dayKey, entrySeconds, fmtDur, fmtTime, humanDay } from '@/lib/flowtime-format';

/** «2026-09-06T14:30» для input[type=datetime-local] по местному времени. */
function toLocalInput(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface DraftEntry {
  id: string | null;
  start: string;
  end: string;
  mode: TimerMode;
  taskId: string;
  projectId: string;
  note: string;
}

function blankDraft(): DraftEntry {
  const now = Date.now();
  return {
    id: null,
    start: toLocalInput(now - 25 * 60_000),
    end: toLocalInput(now),
    mode: 'focus',
    taskId: '',
    projectId: '',
    note: '',
  };
}

export function FlowLog({ flow }: { flow: FlowTimeApi }) {
  const t = useTranslations('dh_flowtime_ui');
  const locale = useLocale();
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<DraftEntry | null>(null);
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...flow.data.entries].sort((a, b) => b.start - a.start);
    if (!q) return list;
    return list.filter((e) => {
      const task = flow.taskById(e.taskId)?.title ?? '';
      const project = flow.projectById(e.projectId)?.name ?? '';
      return `${task} ${project} ${e.note}`.toLowerCase().includes(q);
    });
  }, [flow, query]);

  const summary = useMemo(() => {
    const focus = filtered.filter((e) => e.mode === 'focus');
    const total = focus.reduce((s, e) => s + entrySeconds(e), 0);
    const days = new Set(focus.map((e) => dayKey(e.start))).size;
    return { total, count: focus.length, days, avg: days > 0 ? total / days : 0 };
  }, [filtered]);

  const groups = useMemo(() => {
    const map = new Map<string, FlowEntry[]>();
    for (const e of filtered) {
      const key = dayKey(e.start);
      const list = map.get(key);
      if (list) list.push(e);
      else map.set(key, [e]);
    }
    return [...map.entries()];
  }, [filtered]);

  const save = () => {
    if (!draft) return;
    const start = new Date(draft.start).getTime();
    const end = new Date(draft.end).getTime();

    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      setError(t('entry_error'));
      return;
    }

    const patch = {
      start,
      end,
      mode: draft.mode,
      taskId: draft.taskId || null,
      projectId: draft.projectId || null,
      note: draft.note.slice(0, 500),
    };

    if (draft.id) flow.updateEntry(draft.id, patch);
    else flow.addEntry({ ...patch, interruptions: 0, completed: true, manual: true });

    setDraft(null);
    setError('');
  };

  const edit = (e: FlowEntry) => {
    setDraft({
      id: e.id,
      start: toLocalInput(e.start),
      end: toLocalInput(e.end),
      mode: e.mode,
      taskId: e.taskId ?? '',
      projectId: e.projectId ?? '',
      note: e.note,
    });
    setError('');
  };

  const draftDuration = (() => {
    if (!draft) return null;
    const start = new Date(draft.start).getTime();
    const end = new Date(draft.end).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return (end - start) / 1000;
  })();

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder={t('log_search')}
          className="flex-1 min-w-[220px] px-3.5 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-500)] transition-colors"
        />
        <button
          type="button"
          onClick={() => {
            setDraft(blankDraft());
            setError('');
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)] transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('log_add')}
        </button>
      </div>

      {/* Итоги */}
      <dl className="flex flex-wrap gap-2.5 mb-6">
        {[
          { label: t('log_total'), value: fmtDur(summary.total, locale) },
          { label: t('log_sessions'), value: String(summary.count) },
          { label: t('log_active_days'), value: String(summary.days) },
          { label: t('log_avg'), value: summary.days > 0 ? fmtDur(summary.avg, locale) : '—' },
        ].map((s) => (
          <div
            key={s.label}
            className="px-4 py-2.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)]"
          >
            <dd className="text-lg font-semibold tabular-nums leading-tight">{s.value}</dd>
            <dt className="text-xs text-[var(--color-text-muted)]">{s.label}</dt>
          </div>
        ))}
      </dl>

      {/* Форма записи */}
      {draft && (
        <div className="mb-6 p-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{draft.id ? t('entry_edit') : t('entry_new')}</h3>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setError('');
              }}
              aria-label={t('entry_close')}
              className="w-8 h-8 grid place-items-center rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <label className="flex flex-col gap-1.5 text-xs text-[var(--color-text-muted)]">
              {t('entry_start')}
              <input
                type="datetime-local"
                value={draft.start}
                onChange={(e) => {
                  setDraft({ ...draft, start: e.target.value });
                }}
                className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-xs text-[var(--color-text-muted)]">
              {t('entry_end')}
              <input
                type="datetime-local"
                value={draft.end}
                onChange={(e) => {
                  setDraft({ ...draft, end: e.target.value });
                }}
                className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
              />
            </label>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <label className="flex flex-col gap-1.5 text-xs text-[var(--color-text-muted)]">
              {t('entry_type')}
              <select
                value={draft.mode}
                onChange={(e) => {
                  setDraft({ ...draft, mode: e.target.value as TimerMode });
                }}
                className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
              >
                {(['focus', 'short', 'long', 'free'] as const).map((m) => (
                  <option key={m} value={m}>
                    {t(`mode_${m}_full`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5 text-xs text-[var(--color-text-muted)]">
              {t('entry_task')}
              <select
                value={draft.taskId}
                onChange={(e) => {
                  const task = flow.taskById(e.target.value || null);
                  setDraft({
                    ...draft,
                    taskId: e.target.value,
                    // Проект подставляется от задачи: указывать его отдельно
                    // человек всё равно забудет, а отчёт по проектам поедет.
                    projectId: task?.projectId ?? draft.projectId,
                  });
                }}
                className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
              >
                <option value="">{t('entry_no_task')}</option>
                {flow.data.tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.done ? '✓ ' : ''}
                    {task.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5 text-xs text-[var(--color-text-muted)]">
              {t('entry_project')}
              <select
                value={draft.projectId}
                onChange={(e) => {
                  setDraft({ ...draft, projectId: e.target.value });
                }}
                className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent-500)]"
              >
                <option value="">{t('entry_no_project')}</option>
                {flow.data.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <input
            type="text"
            value={draft.note}
            onChange={(e) => {
              setDraft({ ...draft, note: e.target.value });
            }}
            placeholder={t('entry_note')}
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-500)] mb-3"
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              className="dh-cta px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {draft.id ? t('entry_save') : t('entry_create')}
            </button>
            <span
              className={`text-xs ${error ? 'text-[var(--color-danger-400)]' : 'text-[var(--color-text-muted)]'}`}
            >
              {error ||
                (draftDuration !== null
                  ? t('entry_duration', { value: fmtDur(draftDuration, locale) })
                  : '')}
            </span>
          </div>
        </div>
      )}

      {/* Записи по дням */}
      {groups.length === 0 ? (
        <p className="py-12 text-center text-[var(--color-text-muted)]">
          {query ? t('log_nothing_found') : t('log_empty')}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map(([key, items]) => {
            const dayFocus = items
              .filter((e) => e.mode === 'focus')
              .reduce((s, e) => s + entrySeconds(e), 0);
            const pomos = items.filter((e) => e.mode === 'focus' && e.completed).length;

            return (
              <section key={key}>
                <header className="flex items-baseline gap-3 px-1 pb-2">
                  <h3 className="font-semibold text-sm">{humanDay(key, locale)}</h3>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {t('pomodoros_short', { count: pomos })}
                  </span>
                  <span className="ml-auto text-sm font-medium tabular-nums">
                    {fmtDur(dayFocus, locale)}
                  </span>
                </header>

                <ul className="flex flex-col gap-1">
                  {items.map((e) => {
                    const task = flow.taskById(e.taskId);
                    const project = flow.projectById(e.projectId);
                    const label =
                      task?.title ??
                      (e.mode === 'focus' ? e.note || t('no_task') : t(`mode_${e.mode}_full`));

                    return (
                      <li
                        key={e.id}
                        className="group flex items-center gap-3 px-3.5 py-2.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] hover:border-[var(--color-text-muted)] transition-colors"
                      >
                        <span
                          className="w-0.5 self-stretch rounded-full shrink-0"
                          style={{ background: project?.color ?? 'var(--color-text-muted)' }}
                        />

                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{label}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)] mt-0.5">
                            {project && <span>{project.name}</span>}
                            {task && e.note && (
                              <span className="truncate max-w-[240px]">{e.note}</span>
                            )}
                            {!e.completed && (
                              <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-elevated)] text-[var(--color-warning-400)]">
                                {t('badge_interrupted')}
                              </span>
                            )}
                            {e.manual && (
                              <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-elevated)]">
                                {t('badge_manual')}
                              </span>
                            )}
                            {e.interruptions > 0 && (
                              <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-elevated)]">
                                {t('badge_distractions', { count: e.interruptions })}
                              </span>
                            )}
                          </div>
                        </div>

                        <span className="font-mono text-xs text-[var(--color-text-muted)] shrink-0 hidden sm:block">
                          {fmtTime(e.start)}–{fmtTime(e.end)}
                        </span>
                        <span className="text-sm font-medium tabular-nums shrink-0 w-24 text-right">
                          {fmtDur(entrySeconds(e), locale)}
                        </span>

                        <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => {
                              edit(e);
                            }}
                            aria-label={t('entry_edit_action')}
                            className="w-7 h-7 grid place-items-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              flow.deleteEntry(e.id);
                            }}
                            aria-label={t('entry_delete_action')}
                            className="w-7 h-7 grid place-items-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger-400)] transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
